<template>
  <form method="post" v-on:submit.prevent="submitForm">
    <fieldset v-if="isGenericCandidate">
      <div class="field">
        <label for="scheg-reference-address">{{ $t('address new accomodation') }}</label>
        <div>
          <input
            id="scheg-reference-address"
            type="text"
            name="ref-address"
            minlength="3"
            maxlength="150"
            v-model="form.referenceAddress"
          />
          <v-form-error :error="form.errors.referenceAddress" />
        </div>
      </div>
    </fieldset>

    <fieldset>
      <div class="field">
        <label>{{ $t('I am') }}</label>
        <div>
          <label class="radio" v-for="candidateType in candidateTypes" :key="candidateType.value">
            <input type="radio" name="type" v-model="form.candidateType" :value="candidateType.value" />
            <span class="label">{{ candidateType.label }}</span>
          </label>
        </div>
      </div>

      <div class="field" v-if="maxNumberOfTenants > 1">
        <label>{{ $t('number of tenants') }}</label>
        <div>
          <label class="radio">
            <input type="radio" :value="1" v-model="form.numberOfTenants" />
            <span class="label">{{ $t('alone') }}</span>
          </label>
          <label class="radio">
            <input type="radio" :value="2" v-model="form.numberOfTenants" />
            <span class="label">{{ $t('2 people (couple)') }}</span>
          </label>
        </div>
      </div>
    </fieldset>

    <div v-if="!hasValidTypeCandidateCombo" class="v-form-error">
      <template v-if="!isAvailableForNonStudents">
        <p>{{ $t('This accommodation is only available to students.') }}</p>
      </template>
      <template v-if="!isAvailableForStudents">
        <p>{{ $t('This accommodation is not available to students.') }}</p>
      </template>
    </div>

    <div v-else>
      <template v-for="(account, index) in form.accounts" :key="`account-${index}`">
        <SchegAccountFields
          v-if="index < form.numberOfTenants"
          :account="account"
          :index="index"
          :errors="formErrors"
          :is-student="isStudent"
          @validate-email="validateEmail"
          @apply-did-you-mean="applyDidYouMean"
        />
      </template>

      <fieldset>
        <header>
          <h2>{{ $t('about me') }}</h2>
        </header>

        <div class="field">
          <label for="scheg-motivation">{{ $t('Why do you want to rent this property?') }}</label>
          <div>
            <textarea id="scheg-motivation" v-model="form.comments.motivation" rows="6" cols="40"></textarea>
            <v-form-error :error="formErrors['comments.motivation']" />
          </div>
        </div>

        <div class="field">
          <label for="scheg-description">{{ $t('Please describe yourself.') }}</label>
          <div>
            <textarea id="scheg-description" v-model="form.comments.description" rows="6" cols="40"></textarea>
            <v-form-error :error="formErrors['comments.description']" />
          </div>
        </div>

        <div class="field">
          <label for="scheg-community">{{
            $t('What do you expect from De Scheg and what can the community expect from you?')
          }}</label>
          <div>
            <textarea id="scheg-community" v-model="form.comments.community" rows="6" cols="40"></textarea>
            <p class="help">
              {{
                $t(
                  'Living in De Scheg means more than just a roof over your head. As a resident, you actively contribute to the sense of community. You provide a small contribution to the community of about four hours per month—in a way that suits you. For example, exercising together, organizing a neighborhood activity, helping a neighbor with groceries or a chore, or simply having a good conversation.',
                )
              }}
            </p>
            <v-form-error :error="formErrors['comments.community']" />
          </div>
        </div>
      </fieldset>

      <fieldset v-if="availableFromLabel && isWithinAcceptStartDateWindow">
        <div class="field">
          <div>
            <label class="checkbox">
              <input type="checkbox" name="accept-start-date" v-model="form.acceptStartDate" value="1" required />
              <span class="label">
                {{
                  isAvailableFromInPast
                    ? $t('lease-start:confirmation-immediate')
                    : $t('lease-start:confirmation', { date: availableFromLabel })
                }}<br />

                {{ $t('lease-start:commitment-notice') }}
              </span>
            </label>
            <v-form-error :error="form.errors.acceptStartDate" />
          </div>
        </div>
      </fieldset>

      <fieldset>
        <div class="field">
          <div>
            <label class="checkbox">
              <input type="checkbox" name="terms" v-model="form.terms" value="1" />
              <span class="label">
                {{ $t('I have read and agree to') }}
                <a :href="windowState.urls['terms-conditions']" target="_blank" rel="noopener noreferrer">{{
                  $t('the terms and conditions')
                }}</a>
                {{ $t('and the') }}
                <a :href="windowState.urls['privacy-statement']" target="_blank" rel="noopener noreferrer">{{
                  $t('privacy statement')
                }}</a
                >.
              </span>
            </label>
            <v-form-error :error="form.errors.terms" />
          </div>
        </div>

        <div class="field">
          <div>
            <label class="checkbox">
              <input type="checkbox" name="truth" v-model="form.truth" value="1" />
              <span class="label">
                {{ $t('I declare that I have completed this form truthfully.') }}
              </span>
            </label>
            <v-form-error :error="form.errors.truth" />
          </div>
        </div>
      </fieldset>

      <fieldset class="actions">
        <button type="submit" :disabled="form.processing">
          {{ $t('next step') }}
        </button>
      </fieldset>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { useForm } from 'laravel-precognition-vue'
import type { ComputedRef } from 'vue'
import { computed, onMounted } from 'vue'

import SchegAccountFields from '/@front:shared/components/fields/SchegAccountFields.vue'
import { provideRegistrationContext } from '/@front:shared/composables/useRegistrationContext'
import { useRegistrationForm } from '/@front:shared/composables/useRegistrationForm'
import { subscriberSchegCandidateType } from '/@front:shared/constants'
import type { Account } from '/@front:shared/registration'

type CandidateType = {
  value: string
  label: string
}

const props = withDefaults(
  defineProps<{
    unitType?: string
    maxNumberOfTenants?: number
    isGenericCandidate?: boolean
    availableFrom?: string
  }>(),
  {
    maxNumberOfTenants: 2,
    isGenericCandidate: false,
    availableFrom: '',
  },
)

const documentLocale = document.documentElement.getAttribute('lang') || 'en-US'
const isLocaleNL = documentLocale.includes('nl')

const availableFromLabel = computed(() => {
  if (!props.availableFrom) {
    return ''
  }
  return new Intl.DateTimeFormat(documentLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(props.availableFrom))
})

const isAvailableFromInPast = computed(() => {
  if (!props.availableFrom) {
    return false
  }
  const available = new Date(props.availableFrom)
  available.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return available < today
})

const windowState = window._state

const isWithinAcceptStartDateWindow = windowState.isWithinAcceptStartDateWindow === true

const candidateTypes = windowState.candidateTypes as CandidateType[]

const isAvailableForStudents = computed(
  () => props.isGenericCandidate || ['scheg-studio-student'].includes(props.unitType as string),
)
const isAvailableForNonStudents = computed(
  () => props.isGenericCandidate || !['scheg-studio-student'].includes(props.unitType as string),
)

const countryList = [...windowState.countries].sort((a, b) => a.title.localeCompare(b.title))
const nationalityList = [...windowState.countries].sort((a, b) => a.demonym.localeCompare(b.demonym))

const form = useForm('post', location.pathname, {
  referenceAddress: '',
  candidateType: isAvailableForNonStudents.value ? candidateTypes?.at(0)?.value : subscriberSchegCandidateType.STUDENT,
  numberOfTenants: 1,
  accounts: [] as Account[],
  comments: {
    motivation: '',
    description: '',
    community: '',
  },
  terms: false,
  truth: false,
  acceptStartDate: false,
})

const { didYouMean, submitForm, validateEmail, applyDidYouMean, padAccountsTo4, fillCurrentProfile } =
  useRegistrationForm(form)

provideRegistrationContext({
  countries: countryList,
  nationalities: nationalityList,
  incomeCategories: windowState.incomeCategories,
  studyInstitutions: windowState.studyInstitutions,
  tenantRelations: windowState.subscriberRelations.tenant,
  backupRelations: windowState.subscriberRelations.backup,
  didYouMean,
  showInsertion: isLocaleNL,
})

const hasValidTypeCandidateCombo = computed(() => {
  if (!isAvailableForStudents.value && form.candidateType === subscriberSchegCandidateType.STUDENT) {
    return false
  }

  if (!isAvailableForNonStudents.value && form.candidateType !== subscriberSchegCandidateType.STUDENT) {
    return false
  }

  return form.numberOfTenants <= props.maxNumberOfTenants
})

const isStudent = computed(() => form.candidateType === subscriberSchegCandidateType.STUDENT)
const formErrors: ComputedRef<Record<string, string>> = computed(() => (form.errors || {}) as Record<string, string>)

padAccountsTo4()

onMounted(() => {
  if (windowState.current) {
    fillCurrentProfile(windowState.current, props.maxNumberOfTenants)
  }
})
</script>
